import React, { useState } from 'react';
import THEME from '../utils/theme';
import { CAT_META, formatBDT, truncate } from '../utils/helpers';
import SectionTag from '../components/SectionTag';
import Loader from '../components/Loader';
import useFetch from '../hooks/useFetch';
import useInView from '../hooks/useInView';
import { FaBookOpen, FaTimes, FaClock, FaMoneyBillWave, FaTag, FaArrowRight } from 'react-icons/fa';

/* ─────────────────────────────────────────
   MODAL — bigger, full image
───────────────────────────────────────── */
function CourseModal({ course, onClose }) {
  if (!course) return null;
  const cat  = CAT_META[course.category] || CAT_META.other;
  const Icon = cat.icon;

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(13,27,42,0.82)',
        backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}>
      <div style={{
        background: 'white',
        borderRadius: 20,
        width: '100%',
        maxWidth: 700,          /* wider modal */
        maxHeight: '94vh',
        overflowY: 'auto',
        boxShadow: '0 40px 100px rgba(0,0,0,0.45)',
        animation: 'modalIn 0.28s cubic-bezier(0.34,1.56,0.64,1)',
      }}>

        {/* ── Image area — 16:9, full image visible ── */}
        <div style={{
          position: 'relative',
          width: '100%',
          paddingTop: '56.25%',   /* 16:9 */
          borderRadius: '20px 20px 0 0',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${cat.color}20, ${cat.color}45)`,
        }}>
          {course.thumbnail ? (
            <img
              src={course.thumbnail}
              alt={course.name}
              style={{
                position: 'absolute',
                top: 0, left: 0,
                width: '100%', height: '100%',
                objectFit: 'contain',       /* contain = no crop, full image */
                objectPosition: 'center',
                display: 'block',
                background: '#111',         /* black letterbox */
              }}
            />
          ) : (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon size={80} color={`${cat.color}70`} />
            </div>
          )}

          {/* Category badge */}
          <div style={{
            position: 'absolute', top: 16, left: 16, zIndex: 2,
            background: cat.color, color: 'white',
            fontSize: 10, fontWeight: 700, letterSpacing: '0.12em',
            textTransform: 'uppercase', padding: '6px 16px', borderRadius: 4,
            fontFamily: "'Syne', sans-serif",
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}>{cat.label}</div>

          {/* Close */}
          <button onClick={onClose} style={{
            position: 'absolute', top: 14, right: 14, zIndex: 2,
            width: 40, height: 40, borderRadius: '50%',
            background: 'rgba(0,0,0,0.60)', border: 'none',
            color: 'white', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.2s, transform 0.2s',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          }}
            onMouseEnter={e => { e.currentTarget.style.background = THEME.red; e.currentTarget.style.transform = 'scale(1.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.60)'; e.currentTarget.style.transform = 'scale(1)'; }}>
            <FaTimes size={16} />
          </button>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: '30px 36px 40px' }}>
          <h2 style={{
            fontFamily: "'Syne', sans-serif", fontWeight: 800,
            fontSize: 24, color: THEME.navy, marginBottom: 20, lineHeight: 1.3,
          }}>{course.name}</h2>

          {/* Stats */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 26 }}>
            {[
              { icon: FaClock,         color: '#5B8DEF', label: 'Duration',   value: course.duration        },
              { icon: FaMoneyBillWave, color: THEME.green, label: 'Fee',      value: formatBDT(course.fee)  },
              { icon: FaTag,           color: cat.color,  label: 'Category',  value: cat.label              },
            ].map(({ icon: I, color, label, value }) => (
              <div key={label} style={{
                flex: 1, minWidth: 150,
                background: '#F7F8FA', borderRadius: 12, padding: '14px 18px',
                border: '1px solid #EEE',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                  <I size={13} color={color} />
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#AAA', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{label}</span>
                </div>
                <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 15, color: THEME.navy }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Description */}
          {course.description && (
            <div style={{ marginBottom: 30 }}>
              <h4 style={{
                fontFamily: "'Syne', sans-serif", fontSize: 11, fontWeight: 700,
                color: '#AAA', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 12,
              }}>About This Course</h4>
              <p style={{ fontSize: 15.5, color: '#4A5568', lineHeight: 1.9, fontFamily: "'Noto Serif', serif" }}>
                {course.description}
              </p>
            </div>
          )}

          {/* CTA */}
          <a href="#apply" onClick={onClose} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            background: THEME.red, color: 'white',
            padding: '16px 32px', borderRadius: 10,
            fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 16,
            textDecoration: 'none', letterSpacing: '0.04em',
            transition: 'background 0.2s, transform 0.15s',
            boxShadow: `0 8px 24px ${THEME.red}50`,
          }}
            onMouseEnter={e => { e.currentTarget.style.background = THEME.redDark; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = THEME.red; e.currentTarget.style.transform = 'translateY(0)'; }}>
            Apply for This Course <FaArrowRight size={15} />
          </a>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity:0; transform: translateY(24px) scale(0.96); }
          to   { opacity:1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

/* ─────────────────────────────────────────
   CARD — 16:9 thumbnail, equal height
───────────────────────────────────────── */
function CourseCard({ course, onSelect }) {
  const cat  = CAT_META[course.category] || CAT_META.other;
  const Icon = cat.icon;

  return (
    <div
      onClick={() => onSelect(course)}
      style={{
        background: 'white', borderRadius: 14, overflow: 'hidden',
        border: '1px solid #EDE7DE', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', height: '100%',
        transition: 'transform 0.22s, box-shadow 0.22s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 20px 50px rgba(0,0,0,0.13)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>

      {/* Thumbnail — 16:9 aspect ratio, img tag */}
      <div style={{
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%',   /* 16:9 */
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${cat.color}15, ${cat.color}35)`,
        flexShrink: 0,
      }}>
        {course.thumbnail ? (
          <img
            src={course.thumbnail}
            alt={course.name}
            loading="lazy"
            style={{
              position: 'absolute', top: 0, left: 0,
              width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: 'center',
              display: 'block',
              transition: 'transform 0.4s ease',
            }}
          />
        ) : (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon size={56} color={`${cat.color}70`} />
          </div>
        )}
        <div style={{
          position: 'absolute', top: 12, left: 12,
          background: cat.color, color: 'white',
          fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
          textTransform: 'uppercase', padding: '5px 13px', borderRadius: 3,
          fontFamily: "'Syne', sans-serif",
          boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
        }}>{cat.label}</div>
      </div>

      {/* Body */}
      <div style={{ padding: '22px 24px 26px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 700, color: THEME.navy, marginBottom: 10, lineHeight: 1.4 }}>
          {course.name}
        </h3>
        <p style={{ fontSize: 13.5, color: '#6B7A8D', lineHeight: 1.7, marginBottom: 18, flex: 1 }}>
          {truncate(course.description || 'Click to view full details.', 110)}
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <span style={{ background: THEME.sand, padding: '6px 13px', borderRadius: 5, color: THEME.navy, fontWeight: 600, fontSize: 13, border: '1px solid #E0D8D0' }}>
            ⏱ {course.duration}
          </span>
          <span style={{ background: THEME.greenLight, padding: '6px 13px', borderRadius: 5, color: THEME.green, fontWeight: 700, fontSize: 13, border: `1px solid ${THEME.green}30` }}>
            {formatBDT(course.fee)}
          </span>
        </div>
        <button style={{
          width: '100%', background: 'transparent',
          border: `2px solid ${cat.color}`, color: cat.color, borderRadius: 8,
          fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 13,
          padding: '10px 0', cursor: 'pointer', letterSpacing: '0.05em',
          transition: 'all 0.2s',
        }}
          onMouseEnter={e => { e.currentTarget.style.background = cat.color; e.currentTarget.style.color = 'white'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = cat.color; }}>
          Course Details →
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   MAIN SECTION
───────────────────────────────────────── */
export default function Courses() {
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const { data: courses, loading } = useFetch(`${API_BASE}/courses`);
  const [ref, visible] = useInView();
  const [selected, setSelected] = useState(null);
  const active = courses.filter(c => c.isActive);

  return (
    <section id="courses" style={{ padding: '100px clamp(20px,4vw,40px)', background: THEME.sand }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div ref={ref} className={`sk-fade-up ${visible ? 'sk-visible' : ''}`}>
          <SectionTag>Our Courses</SectionTag>
          <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 'clamp(2rem,4vw,3.2rem)', lineHeight: 1.1, color: THEME.navy }}>
            Choose Your Path to Japan
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0 48px' }}>
            <span style={{ fontFamily: "'Noto Serif', serif", fontSize: 13, color: `${THEME.red}80`, letterSpacing: '0.1em' }}>
              コースを選んでください
            </span>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${THEME.red}40, transparent)` }} />
          </div>
        </div>

        {loading ? <Loader /> : active.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 80, color: '#999' }}>
            <FaBookOpen size={48} color="#CCC" style={{ display: 'block', margin: '0 auto 12px' }} />
            <p>New courses coming soon</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 28, alignItems: 'stretch' }}>
            {active.map((c, i) => (
              <div key={c._id} className={`sk-fade-up ${visible ? 'sk-visible' : ''}`}
                style={{ transitionDelay: `${i * 0.08}s`, display: 'flex' }}>
                <CourseCard course={c} onSelect={setSelected} />
              </div>
            ))}
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 52 }}>
          <a href="#apply" style={{
            background: THEME.red, color: 'white', border: 'none',
            padding: '14px 36px', borderRadius: 6,
            fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 15,
            textDecoration: 'none', display: 'inline-block', letterSpacing: '0.04em',
            boxShadow: `0 8px 24px ${THEME.red}40`,
          }}>Apply for Any Course</a>
        </div>
      </div>

      {selected && <CourseModal course={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}
